// src/pages/LecturePrivacy.js
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import PageContainer from "../components/Layout/PageContainer";
import SeoHead from "../components/SeoHead";
import { useLanguage } from "../i18n/LanguageContext";
import LectureFaqMenu from "../components/LectureFaqMenu";
import "../styles/lectureUpload.css";

function detectUiLocale(navUploadLabel) {
  if (!navUploadLabel) return "en";
  if (navUploadLabel.includes("上传")) return "zh-Hans";
  if (/[\u4e00-\u9fff]/.test(navUploadLabel)) return "zh-Hant";
  return "en";
}

const LecturePrivacy = () => {
  const { t } = useLanguage();
  const navUploadLabel = t ? t("navUpload") : "Upload";
  const uiLocale = detectUiLocale(navUploadLabel);

  const strings = useMemo(() => {
    return {
      en: {
        headTitle: "Lecture STT · Privacy & Data Policy",
        headDesc:
          "Learn how Lecture STT collects, processes, and protects your data: account info, uploaded audio files, transcripts, retention, security and your rights.",
        pageTitle: "Privacy & Data Policy",
        lastUpdated: "Last updated: 02 Dec 2025 (Beta)",

        // Left menu
        faqMenuTitle: "STT FAQ Menu",
        faqMenuHowToUse: "How to Use",
        faqMenuTips: "Recording & Upload Tips",
        faqMenuLanguage: "Supported Languages",
        faqMenuPrivacy: "Privacy & Data",
        faqMenuVersion: "Version & Updates",
        faqMenuSupport: "Support / Contact",
        faqMenuAll: "View all STT FAQs",

        s1: "1. Introduction",
        p1:
          "By using the Lecture STT service, you agree to this Privacy & Data Policy. We aim to handle your data responsibly, transparently, and only for purposes needed to operate this service.",

        s2: "2. What we collect",
        p2a: "a) Basic personal data",
        p2a1:
          "When you sign in with Google, we receive your basic profile information (name, email, Google ID). This is used to authenticate you and link your lectures and transcripts to your account.",

        p2b: "b) Audio files & transcripts",
        p2b1:
          "When you upload a lecture, we temporarily store your audio file, the generated transcript, and any optional summaries to provide the STT service. We do NOT use your audio or transcripts to train AI models without your explicit permission.",

        p2c: "c) Non-personal technical data",
        p2c1:
          "We collect limited technical information (e.g. IP address, browser/OS version, device type, upload timestamps, processing error codes, performance and diagnostic logs) to maintain and improve service quality.",

        s3: "3. How we use data",
        l3: [
          "Process your uploaded audio and generate transcripts and summaries.",
          "Show lecture status, history, and allow you to re-download results.",
          "Maintain system stability, uptime, and processing performance.",
          "Detect, prevent, and investigate abuse or misuse of the service.",
          "Perform basic, anonymised analytics that do not identify individual users.",
        ],

        s4: "4. Storage & retention",
        p4:
          "We keep data for the shortest reasonable period needed to operate and protect the service:",
        l4: [
          <>
            <strong>Audio files</strong>: stored only for processing and re-download
            purposes, and automatically deleted within <strong>30 days</strong> after
            transcription is completed, regardless of subscription status.
          </>,
          <>
            <strong>Transcripts & summaries</strong>: retained for up to{" "}
            <strong>30 days</strong> after they are generated, then automatically deleted.
          </>,
          <>
            <strong>Operational metadata</strong> (timestamps, duration, error codes,
            without transcript content): typically retained for up to{" "}
            <strong>30 days</strong> for system stability and troubleshooting.
          </>,
          <>
            <strong>User-initiated deletion</strong>: if you wish to delete data earlier,
            you may submit a request via{" "}
            <Link to="/lecture/faq/support" className="text-blue-600 underline">
              Support / Contact
            </Link>
            . Deletion will be processed within a reasonable time.
          </>,
          <>
            <strong>Exceptions</strong>: where required by law or security investigations,
            certain data may be kept for up to <strong>180 days</strong> and deleted once
            no longer needed.
          </>,
        ],

        s5: "5. Your rights",
        l5: [
          <>
            <strong>Access / correct / delete</strong>: you may request to access, correct,
            or delete your transcripts or account-related data (subject to legal
            limitations).
          </>,
          <>
            <strong>Delete audio / transcripts</strong>: you may delete any lecture entry
            from the Status page; associated audio and transcript data will be removed from
            our systems within a reasonable time.
          </>,
          <>
            Requests can be submitted via{" "}
            <Link to="/lecture/faq/support" className="text-blue-600 underline">
              Support / Contact
            </Link>
            .
          </>,
        ],

        s6: "6. Responsible use",
        p6:
          "Please do not upload content that is illegal, infringes others’ rights, or contains highly sensitive personal information. Content must not include harmful, abusive, deceptive, or prohibited material. Violations may result in restrictions or disabling of the service.",

        s7: "7. Data sharing & third-party services",
        p7:
          "We may use trusted service providers (e.g. cloud hosting, storage, email, monitoring, abuse detection) to operate this service. These providers may access data only where strictly necessary to operate the service and must follow reasonable security practices. We do not sell personal data or use it for advertising.",

        s8: "8. Cross-border transfers",
        p8:
          "Because we use cloud infrastructure, your data may be processed or stored in data centres located outside your region. We take reasonable measures to safeguard your data during cross-border transfers.",

        s9: "9. Cookies & tracking",
        p9:
          "We may use cookies or similar technologies to support sign-in, improve stability, and enable limited analytics:",
        l9: [
          "Keep your login session active and secure.",
          "Support upload, status tracking, and processing queues.",
          "Perform basic performance monitoring and usage analytics (if enabled).",
        ],
        p9b:
          "You may disable cookies in your browser, but some features of the Service may not function correctly without them.",

        s10: "10. Security",
        p10:
          "We implement reasonable technical and organisational security measures to reduce the risk of unauthorised access, misuse, or loss of data. However, no online service can be 100% secure. If a security incident occurs, we will take appropriate action and, where necessary, notify affected users.",

        s11: "11. Changes to this Policy",
        p11:
          "We may update this Privacy & Data Policy from time to time, with a new effective date shown above. Your continued use of the Service after changes take effect will constitute acceptance of the updated Policy.",

        s12: "12. Contact",
        p12: (
          <>
            Have questions about privacy or data? Please contact us via{" "}
            <Link to="/lecture/faq/support" className="text-blue-600 underline">
              Support / Contact
            </Link>
            .
          </>
        ),
      },

      // ---------- Traditional Chinese ----------
      "zh-Hant": {
        headTitle: "Lecture STT 隱私與資料政策",
        headDesc:
          "了解 Lecture STT 如何處理你的音訊、逐字稿與個人資料，包括蒐集方式、使用目的、保留期限、安全措施與你的權利。",
        pageTitle: "隱私與資料政策",
        lastUpdated: "最後更新：2025 年 12 月 2 日（Beta）",

        faqMenuTitle: "STT 常見問題選單",
        faqMenuHowToUse: "如何使用",
        faqMenuTips: "錄音與上傳小貼士",
        faqMenuLanguage: "支援語言",
        faqMenuPrivacy: "隱私與資料安全",
        faqMenuVersion: "版本與更新記錄",
        faqMenuSupport: "支援／聯絡我們",
        faqMenuAll: "查看全部 STT 常見問題",

        s1: "1. 簡介",
        p1:
          "使用 Lecture STT 服務即表示你同意本隱私與資料政策。我們致力以負責任、透明、且僅限於提供服務所需的方式處理你的資料。",

        s2: "2. 我們蒐集哪些資料",
        p2a: "a）基本個人資料",
        p2a1:
          "當你使用 Google 登入時，我們會取得你的基本資料（姓名、電郵、Google ID），用於驗證身分，並將講座與逐字稿與你的帳號關聯。",

        p2b: "b）上傳音訊與逐字稿",
        p2b1:
          "當你上載課堂錄音時，我們會暫時儲存音訊檔、系統產生的逐字稿，以及（如有）摘要內容，以提供語音轉筆記功能。除非另行載明或徵得你的明確同意，我們不會將錄音或逐字稿用於訓練 AI 模型。",

        p2c: "c）非個人技術資料",
        p2c1:
          "我們會收集部分技術資訊（例如 IP 位址、瀏覽器／作業系統版本、裝置類型、上載及處理時間、錯誤代碼、效能與診斷紀錄），用於維護及改善系統品質。",

        s3: "3. 我們如何使用資料",
        l3: [
          "將錄音轉換為逐字稿及摘要，並顯示處理狀態。",
          "讓你在狀態頁查看歷史記錄與下載結果。",
          "維持系統穩定性與效能（排程、佇列、錯誤偵測）。",
          "偵測、預防及調查濫用或異常使用情況。",
          "在不識別個別使用者的前提下進行基礎匿名分析。",
        ],

        s4: "4. 資料儲存與保留期限",
        p4: "我們會在提供與保護服務所需的最短合理期間內保留資料：",
        l4: [
          <>
            <strong>音訊檔</strong>：僅用於轉錄與重新下載用途，
            在完成轉錄後最多保留 <strong>30 天</strong>，
            不論是否為付費用戶，期滿後會自動刪除。
          </>,
          <>
            <strong>逐字稿與摘要</strong>：於產生後最多保留{" "}
            <strong>30 天</strong>，之後將自動刪除。
          </>,
          <>
            <strong>營運中繼資料</strong>（如時間戳、錄音長度、錯誤代碼，
            不包含逐字內容）：通常保留最多 <strong>30 天</strong>，
            用於系統穩定與問題排查。
          </>,
          <>
            <strong>使用者提出刪除請求</strong>：如需提前刪除資料，
            請透過{" "}
            <Link to="/lecture/faq/support" className="text-blue-600 underline">
              支援／聯絡我們
            </Link>{" "}
            提交申請，我們會於合理時間內處理。
          </>,
          <>
            <strong>例外情況</strong>：如因法律義務或安全調查需要，相關資料可能延長保存至{" "}
            <strong>180 天</strong>，並於不再需要時刪除。
          </>,
        ],

        s5: "5. 你的權利",
        l5: [
          <>
            <strong>存取／更正／刪除</strong>：你可請求存取、更正或刪除你的逐字稿及帳號相關資料（須符合法律規定）。
          </>,
          <>
            <strong>刪除錄音與逐字稿</strong>：你可在狀態頁刪除任何講座；相關音訊及逐字稿會在合理時間內從系統移除。
          </>,
          <>
            如需提出請求，請透過{" "}
            <Link to="/lecture/faq/support" className="text-blue-600 underline">
              支援／聯絡我們
            </Link>{" "}
            與我們聯絡。
          </>,
        ],

        s6: "6. 使用者責任",
        p6:
          "請勿上載違反法律、侵害他人權利，或包含高度敏感個人資料的內容；亦不得包含仇恨、暴力、色情、詐騙或其他有害內容。如有違反，本服務可能採取限制或停用措施。",

        s7: "7. 資料分享與第三方服務",
        p7:
          "我們會委託受信任的雲端及相關服務供應商（例如雲端主機、儲存、郵件、監控、濫用偵測）協助營運。第三方僅能在提供服務所必需的最低範圍內存取資料，並必須遵守合理的安全要求。我們不會販售你的個人資料，也不會將其用於廣告投放。",

        s8: "8. 跨境資料傳輸",
        p8:
          "由於採用雲端架構，你的資料可能在境外的數據中心處理或儲存。我們會在合理範圍內採取措施保障跨境傳輸過程中的資料安全。",

        s9: "9. Cookie 與追蹤技術",
        p9:
          "本服務可能使用 Cookie 或類似技術，以支援登入與提升穩定性，包括：",
        l9: [
          "維持登入狀態與安全性。",
          "支援上載、狀態查詢與處理佇列流程。",
          "進行基礎效能監控與使用情況分析（如有啟用）。",
        ],
        p9b: "你可以在瀏覽器中停用 Cookie，但部分功能可能因此無法正常運作。",

        s10: "10. 資料安全",
        p10:
          "我們採用合理的技術與管理措施，降低資料遭未經授權存取、濫用或遺失的風險。然而，任何線上系統都無法保證 100% 安全；如發生資安事故，我們會採取適當行動，並在有需要時通知受影響的使用者。",

        s11: "11. 本政策之變更",
        p11:
          "隨著服務發展，我們可能更新本隱私與資料政策，並於頁面上標示新的生效日期。你在更新後持續使用本服務，即視為同意更新後的政策。",

        s12: "12. 聯絡我們",
        p12: (
          <>
            若你對隱私或資料使用有任何疑問，歡迎透過{" "}
            <Link to="/lecture/faq/support" className="text-blue-600 underline">
              支援／聯絡我們
            </Link>{" "}
            與我們聯絡。
          </>
        ),
      },

      // ---------- Simplified Chinese ----------
      "zh-Hans": {
        headTitle: "Lecture STT 隐私与数据政策",
        headDesc:
          "了解 Lecture STT 如何处理你的音频、逐字稿和个人信息，包括收集方式、使用目的、保留期限、安全措施以及你的权利。",
        pageTitle: "隐私与数据政策",
        lastUpdated: "最后更新：2025 年 12 月 2 日（Beta）",

        faqMenuTitle: "STT 常见问题菜单",
        faqMenuHowToUse: "如何使用",
        faqMenuTips: "录音与上传小贴士",
        faqMenuLanguage: "支持语言",
        faqMenuPrivacy: "隐私与数据安全",
        faqMenuVersion: "版本与更新记录",
        faqMenuSupport: "客服／联系我们",
        faqMenuAll: "查看全部 STT 常见问题",

        s1: "1. 简介",
        p1:
          "使用 Lecture STT 服务即表示你同意本隐私与数据政策。我们会以负责、透明、并仅限于提供服务所需的方式处理你的数据。",

        s2: "2. 我们收集哪些数据",
        p2a: "a）基本个人信息",
        p2a1:
          "当你使用 Google 登录时，我们会获得你的基本信息（姓名、邮箱、Google ID），用于身份验证，并将你的课堂与逐字稿与帐号关联。",

        p2b: "b）上传音频与逐字稿",
        p2b1:
          "当你上传课堂录音时，我们会暂时保存音频文件、系统生成的逐字稿以及（如有）摘要，用于提供语音转文字与摘要服务。除非另行说明或获得你的明确同意，我们不会将音频或逐字稿用于训练 AI 模型。",

        p2c: "c）非个人技术信息",
        p2c1:
          "我们会收集部分技术信息（例如 IP 地址、浏览器／系统版本、设备类型、上传和处理时间、错误代码、性能与诊断日志），用于维护和改进系统表现。",

        s3: "3. 我们如何使用数据",
        l3: [
          "将音频转换为逐字稿和摘要，并显示处理状态。",
          "让你在状态页查看历史记录并重新下载结果。",
          "维护系统稳定性与性能（排队、错误检测、性能优化）。",
          "检测、防止和调查滥用或异常使用行为。",
          "进行不识别个人用户的基础匿名统计分析。",
        ],

        s4: "4. 数据存储与保留期限",
        p4: "我们会在提供与保护服务所需的最短合理期限内保留数据：",
        l4: [
          <>
            <strong>音频文件</strong>：仅用于转录与重新下载，
            在完成转录后最多保留 <strong>30 天</strong>，
            无论是否为订阅用户，期满后将自动删除。
          </>,
          <>
            <strong>逐字稿与摘要</strong>：在生成后最多保留{" "}
            <strong>30 天</strong>，随后自动删除。
          </>,
          <>
            <strong>运营元数据</strong>（如时间戳、录音时长、错误代码，
            不包含逐字内容）：通常最多保留 <strong>30 天</strong>，
            用于系统稳定性与故障排查。
          </>,
          <>
            <strong>用户提出删除请求</strong>：如需提前删除数据，
            请通过{" "}
            <Link to="/lecture/faq/support" className="text-blue-600 underline">
              客服／联系我们
            </Link>{" "}
            提交申请，我们将在合理时间内处理。
          </>,
          <>
            <strong>例外情况</strong>：如因法律义务或安全调查需要，相关数据可能延长保存至{" "}
            <strong>180 天</strong>，并在不再需要时删除。
          </>,
        ],

        s5: "5. 你的权利",
        l5: [
          <>
            <strong>访问／更正／删除</strong>：你可以请求访问、更正或删除你的逐字稿及帐号相关数据（需符合法律规定）。
          </>,
          <>
            <strong>删除音频与逐字稿</strong>：你可以在状态页删除课堂；相关音频及逐字稿会在合理时间内从系统移除。
          </>,
          <>
            如需提出请求，请通过{" "}
            <Link to="/lecture/faq/support" className="text-blue-600 underline">
              客服／联系我们
            </Link>{" "}
            页面与我们联系。
          </>,
        ],

        s6: "6. 使用者责任",
        p6:
          "请不要上传违法、侵权或包含高度敏感个人信息的内容；也不得上传仇恨、暴力、色情、欺诈或其他有害内容。如有违反，本服务可能采取限制或停用等措施。",

        s7: "7. 数据共享与第三方服务",
        p7:
          "我们会使用受信任的云服务和相关服务提供商（如云主机、存储、邮件、监控、滥用检测）来运营服务。第三方只能在提供服务所必需的范围内访问数据，并必须遵守合理的安全要求。我们不会出售你的个人数据，也不会将其用于广告投放。",

        s8: "8. 跨境数据传输",
        p8:
          "由于采用云端架构，你的数据可能在境外的数据中心处理或存储。我们会在合理范围内采取措施，保障跨境传输过程中的数据安全。",

        s9: "9. Cookie 与追踪技术",
        p9:
          "本服务可能使用 Cookie 或类似技术，以支持登录并提升稳定性，包括：",
        l9: [
          "维持登录状态和会话安全。",
          "支持上传、状态查询和处理队列流程。",
          "进行基础性能监控和使用情况分析（如有启用）。",
        ],
        p9b:
          "你可以在浏览器中关闭 Cookie，但这可能导致部分功能无法正常使用。",

        s10: "10. 数据安全",
        p10:
          "我们采取合理的技术和管理措施，降低数据被未授权访问、滥用或泄露的风险。但任何在线系统都无法保证 100% 安全；如发生安全事件，我们会采取相应措施，并在必要时通知受影响的用户。",

        s11: "11. 本政策的变更",
        p11:
          "随着服务的发展，我们可能更新本隐私与数据政策，并在页面上标明新的生效日期。你在更新后继续使用本服务，即视为同意更新后的政策。",

        s12: "12. 联系我们",
        p12: (
          <>
            如果你对隐私或数据使用有任何疑问，欢迎通过{" "}
            <Link to="/lecture/faq/support" className="text-blue-600 underline">
              客服／联系我们
            </Link>{" "}
            页面与我们联系。
          </>
        ),
      },
    }[uiLocale];
  }, [uiLocale]);


  const url = "https://stt.aittsservice.online/lecture/faq/privacy";

  return (
    <PageContainer>
      <SeoHead
        title={strings.headTitle}
        description={strings.headDesc}
        url={url}
      />

      {/* Hero */}
      <div className="card upload-hero">
      <div className="card-heading">
        <h1 className="card-title">{strings.pageTitle}</h1>
      </div>
        <p style={{ fontSize: 12, marginTop: 4 }}>{strings.lastUpdated}</p>
      </div>

      {/* Layout: same as How-to-Use */}
      <div className="card upload-card upload-faq-card">
        <div className="upload-layout upload-layout--faq howto-grid">
          {/* Left menu */}
          <LectureFaqMenu active="privacy" />


          {/* Right side content */}
          <main className="upload-main upload-main--faq howto-main">
            <section className="faq-section">
              <h2>{strings.s1}</h2>
              <p>{strings.p1}</p>
            </section>

            <section className="faq-section">
              <h2>{strings.s2}</h2>
              <p>
                <strong>{strings.p2a}</strong>
              </p>
              <p>{strings.p2a1}</p>

              <p>
                <strong>{strings.p2b}</strong>
              </p>
              <p>{strings.p2b1}</p>

              <p>
                <strong>{strings.p2c}</strong>
              </p>
              <p>{strings.p2c1}</p>
            </section>

            <section className="faq-section">
              <h2>{strings.s3}</h2>
              <ul>
                {strings.l3.map((x, i) => (
                  <li key={i}>{x}</li>
                ))}
              </ul>
            </section>

            <section className="faq-section">
              <h2>{strings.s4}</h2>
              <p>{strings.p4}</p>
              <ul>
                {strings.l4.map((x, i) => (
                  <li key={i}>{x}</li>
                ))}
              </ul>
            </section>

            <section className="faq-section">
              <h2>{strings.s5}</h2>
              <ul>
                {strings.l5.map((x, i) => (
                  <li key={i}>{x}</li>
                ))}
              </ul>
            </section>

            <section className="faq-section">
              <h2>{strings.s6}</h2>
              <p>{strings.p6}</p>
            </section>

            <section className="faq-section">
              <h2>{strings.s7}</h2>
              <p>{strings.p7}</p>
            </section>

            <section className="faq-section">
              <h2>{strings.s8}</h2>
              <p>{strings.p8}</p>
            </section>

            <section className="faq-section">
              <h2>{strings.s9}</h2>
              <p>{strings.p9}</p>
              <ul>
                {strings.l9.map((x, i) => (
                  <li key={i}>{x}</li>
                ))}
              </ul>
              <p>{strings.p9b}</p>
            </section>

            <section className="faq-section">
              <h2>{strings.s10}</h2>
              <p>{strings.p10}</p>
            </section>

            <section className="faq-section">
              <h2>{strings.s11}</h2>
              <p>{strings.p11}</p>
            </section>

            <section className="faq-section">
              <h2>{strings.s12}</h2>
              <p>{strings.p12}</p>
            </section>
          </main>
        </div>
      </div>
    </PageContainer>
  );
};

export default LecturePrivacy;
